import React, { useContext } from 'react';
import Styled from 'styled-components';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import Hero from '@ecosystems/landing_pages/hero/Hero';
import {
  TextWrapper,
  TextWrapperInner,
  Title,
  SubTitle,
  VideoWrapper,
  Disclaimer,
} from '@ecosystems/landing_pages/hero/components';
import { Flex } from '@components/layout/Grid';
import MetaTags from '@components/MetaTags';
import Button from '@components/buttons';
import { ParsedUrlQuery } from 'querystring';
import PricingSection from '@ecosystems/pricing/PricingSection';
import {
  GenericProgramsDocument,
  GenericProgramSortAttribute,
  LanguageKind,
  ProgramCategory,
  Video,
} from '@gql/generated';
import LanguageContext from '@lib/contexts/languageContext';
import Trailer from '@organisms/video-player/Trailer';
import FAQSection from '@ecosystems/programs_listing/FAQSection';
import MessageStripe, { Link_ } from '@components/MessageStripe';
import { ProgramsSearch } from '@ecosystems/programs_listing/ProgramsSearch';
import { UserContext } from '@lib/contexts/UserProvider';

const HeroSection = Styled(Hero)`
  width: 100%;
  max-width: 1280px;

  ${TextWrapper} {
    padding-right: 8%;
    margin: 0 auto;
    z-index: 1;

    ${TextWrapperInner} {
      max-width: 720px;
    }
    
    ${Title} {
      display: flex;
      align-items: center;
      font-size: 34px;
      color: #243240;
      line-height: 1.2;
      margin-bottom: 0px;

      span {
        font-size: 11px;
        color: ${({ theme }) => theme.colors.neutrals.white};
        font-weight: 500;
        border-radius: 3px;
        padding: 5px;
        margin-left: 12px;
        background-color: #129EDC;
      }
    }

    ${SubTitle} {
      font-size: 20px;
      line-height: 1.5;
      margin-top: 24px;
      margin-bottom: 24px;
    }

    ${Disclaimer} {
      font-size: 13px;
      margin-top: 10px;
    }

    @media (max-width: ${props => props.theme.viewports.tablet}px) {
      ${Title} {
        font-size: 24px;
      }
      margin-top: 20px;
    }

    @media (max-width: ${props => props.theme.viewports.mobile}px) {
      padding-right: 0;
    }
  }

  ${VideoWrapper} {
    display: flex;
    align-items: center;
    border-radius: 5px;
    overflow: hidden;
  }
`;

const WalkThroughSection = Styled(Flex)`
  background-color: #F0F5FB;

  ${TextWrapper}{
      ${SubTitle} {
      margin-bottom: 0;
    }
  }
`;

const FaqButton = Styled(Button)`
  color: ${({ theme }) => theme.colors.secondary['gray-5']};
  border: 1px solid ${({ theme }) => theme.colors.secondary['gray-5']};;
  padding: 11px 24px;
  margin-left: 4px;
  background: white;

  :hover {
    background: white;
  }
`;

const heroVideo = {
  id: '52f0d8b2-1f91-48a2-aae5-9f94ecaa3ece',
  identifier: 'k2g3',
  content: { title: 'program listing page trailer' },
  hls:
    'https://yogobe.23video.com/64968558/71570629/7fe60ee307a76da35b6893f291f21bb2/video_hls/playlist.m3u8',
  thumbnailUrl:
    'https://yogobe.23video.com/64968558/71570629/e0cd1296fce79b49f16c7f11689368e0//programoverview1920x1080onsitemp4-11-thumbnail.jpglarge/embed/thumbnail.jpg',
  player: {
    hasAccess: true,
    assets: [
      {
        src:
          'https://yogobe.23video.com/64968558/71570629/7fe60ee307a76da35b6893f291f21bb2/video_mobile_high/programoverview1920x1080onsitemp4-11-video.mp4',
        resolution: 180,
      },
      {
        src:
          'https://yogobe.23video.com/64968558/71570629/7fe60ee307a76da35b6893f291f21bb2/video_medium/programoverview1920x1080onsitemp4-11-video.mp4',
        resolution: 360,
      },
      {
        src:
          'https://yogobe.23video.com/64968558/71570629/7fe60ee307a76da35b6893f291f21bb2/video_hd/programoverview1920x1080onsitemp4-11-video.mp4',
        resolution: 720,
      },
      {
        src:
          'https://yogobe.23video.com/64968558/71570629/7fe60ee307a76da35b6893f291f21bb2/video_1080p/programoverview1920x1080onsitemp4-11-video.mp4',
        resolution: 1080,
      },
    ],
  },
} as Video;

const programWalkthrough = {
  id: '4eb82b44-5947-4f35-9b86-6637f3ee1377',
  identifier: '8we5',
  hls:
    'https://yogobe.23video.com/64968561/71881363/6b726d1b7a8d87ab869a60b7964486cb/video_hls/playlist.m3u8',
  thumbnailUrl:
    'https://yogobe.23video.com/64968561/71881363/4d159ec4b76d0c07c042aa40bdcae7a7//1920x1080program-tutorial1mp4-8-thumbnail.jpglarge/embed/thumbnail.jpg',
  player: {
    hasAccess: true,
    assets: [
      {
        src:
          'https://yogobe.23video.com/64968561/71881363/6b726d1b7a8d87ab869a60b7964486cb/video_mobile_high/1920x1080program-tutorial1mp4-8-video.mp4',
        resolution: 180,
      },
      {
        src:
          'https://yogobe.23video.com/64968561/71881363/6b726d1b7a8d87ab869a60b7964486cb/video_medium/1920x1080program-tutorial1mp4-8-video.mp4',
        resolution: 360,
      },
      {
        src:
          'https://yogobe.23video.com/64968561/71881363/6b726d1b7a8d87ab869a60b7964486cb/video_hd/1920x1080program-tutorial1mp4-8-video.mp4',
        resolution: 720,
      },
      {
        src: 'https://yogobe.23video.com',
        resolution: 1080,
      },
    ],
  },
} as Video;

export const buildProgramsFilters = (query: ParsedUrlQuery) => {
  const languages = query?.languages
    ? [query.languages].flat()
    : ['en', 'se', 'no', 'fi'];
  return {
    search: (query?.search as string) || '',
    languages: languages as LanguageKind[],
    instructor: (query?.instructor as string) || null,
    theme: (query?.theme as string) || null,
    category: null,
    sortBy:
      (query?.sortBy as GenericProgramSortAttribute) ||
      GenericProgramSortAttribute['NewestFirst'],
    tags: null,
  };
};

const ProgramsListings = ({ pageProps }) => {
  const lang = useContext(LanguageContext);
  const [currentUser] = useContext(UserContext);

  return (
    <>
      <MetaTags
        title={t`programs_2021.listing_page.meta.title`}
        description={t`programs_2021.listing_page.meta.description`}
        thumbnailUrl="/static/programs_meta_image.jpg"
      />
      <MessageStripe bg="#e2d2af" color="#242424">
        <Trans
          id="programs_2021.listing_page.legacy_programs_banner"
          components={[<Link_ key="0" href={`/${lang}/program-old`}></Link_>]}
        />
      </MessageStripe>
      <Flex justifyContent="center" px={20} py={[0, 20, 40]}>
        <HeroSection
          title={<Trans id="programs_2021.listings_page.hero_title" />}
          subtitle={<Trans id="programs_2021.listings_page.subtitle" />}
          actions={[
            {
              name: (
                <Trans id="programs_2021.listings_page.explore_programs_btn" />
              ),
              theme: 'stone',
              href: '#explore-programs',
            },
            <FaqButton key="faq" href="#faq">
              <Trans id="programs_2021.listings_page.faq_btn" />
            </FaqButton>,
          ]}
          video={
            <Trailer
              {...{
                video: heroVideo,
                thumbnailImage: heroVideo.thumbnailUrl,
                isLimited: false,
                autoPlay: false,
                videoSources: heroVideo.player.assets,
                elementID: heroVideo.id,
              }}
            />
          }
          disclaimer={
            !currentUser?.isPremium ? (
              <Trans id="programs_2021.listing_page.hero.no_premium_disclaimer" />
            ) : (
              <Trans id="programs_2021.listing_page.hero.premium_disclaimer" />
            )
          }
          reverseColumns
        />
      </Flex>
      <div id="explore-programs">
        <ProgramsSearch
          initialFilters={pageProps.initialFilters}
          ssrData={pageProps.ssrPrograms}
          programCategory={ProgramCategory['Programs']}
        />
        {['se', 'no'].includes(lang) ? (
          <WalkThroughSection
            justifyContent="center"
            px={20}
            py={[0, 20, 40]}
            mb={20}
          >
            <HeroSection
              title={<Trans id="programs_listing.walkthrough_section.title" />}
              subtitle={
                <Trans id="programs_listing.walkthrough_section.subtitle" />
              }
              video={
                <Trailer
                  {...{
                    video: programWalkthrough,
                    thumbnailImage: programWalkthrough.thumbnailUrl,
                    isLimited: false,
                    autoPlay: false,
                    videoSources: programWalkthrough.player.assets,
                    elementID: programWalkthrough.id,
                  }}
                />
              }
            />
          </WalkThroughSection>
        ) : null}
        <FAQSection />
        <div>
          {currentUser?.isNoneTrialPremium ? null : (
            <PricingSection id="subscription-offers" />
          )}
        </div>
      </div>
    </>
  );
};

export const getServerSideProps = async ({ req, query }) => {
  const filters = buildProgramsFilters(query);
  const initialFilters = {
    ...filters,
    category: ProgramCategory['Programs'],
    search: filters.search === '' ? '*' : filters.search,
    sortBy: !filters.sortBy
      ? GenericProgramSortAttribute['NewestFirst']
      : filters.sortBy,
  };

  const { data } = await req.apolloClient.query({
    query: GenericProgramsDocument,
    variables: {
      page: 1,
      ...initialFilters,
    },
  });

  return {
    props: {
      initialFilters,
      ssrPrograms: data?.genericPrograms,
    },
  };
};

export default ProgramsListings;
